/**
 * Overlay system types for DrawingJS.
 * Handles temporary preview graphics rendered on top of the main canvas.
 */

import type { SnapOptions } from "../snap/types";
import type { PointerType } from "../interaction/types";

// ============== Overlay Geometry Types ==============

/**
 * Base interface for all overlay geometry.
 */
export interface OverlayGeometry {
  /** Unique identifier */
  id: string;
  /** Geometry type */
  type: OverlayType;
  /** Style for rendering */
  style: OverlayStyle;
}

/**
 * Types of overlay geometry.
 */
export enum OverlayType {
  Point = "point",
  Line = "line",
  Polyline = "polyline",
  Circle = "circle",
  Arc = "arc",
  Rectangle = "rectangle",
  Text = "text",
  Ellipse = "ellipse",
  // Interactive types
  Handle = "handle",
  Ruler = "ruler",
}

/**
 * Coordinate system for overlay elements.
 */
export enum CoordinateSystem {
  /** Fixed screen position (default for existing overlays) */
  Screen = "screen",
  /** World coordinates - tracks with pan/zoom */
  World = "world",
}

/**
 * Style options for overlay rendering.
 */
export interface OverlayStyle {
  /** Stroke color (CSS color string) */
  strokeColor?: string;
  /** Stroke width in pixels */
  strokeWidth?: number;
  /** Fill color (CSS color string) */
  fillColor?: string;
  /** Line dash pattern [dashLength, gapLength] */
  lineDash?: number[];
  /** Opacity (0-1) */
  opacity?: number;
}

/**
 * Default overlay style.
 */
export const DEFAULT_OVERLAY_STYLE: OverlayStyle = {
  strokeColor: "#0088ff",
  strokeWidth: 2,
  fillColor: undefined,
  lineDash: [],
  opacity: 1,
};

// ============== Geometry Definitions ==============

export interface PointOverlay extends OverlayGeometry {
  type: OverlayType.Point;
  /** Screen coordinates */
  x: number;
  y: number;
  /** Point radius in pixels */
  radius?: number;
}

export interface LineOverlay extends OverlayGeometry {
  type: OverlayType.Line;
  /** Start point (screen coordinates) */
  x1: number;
  y1: number;
  /** End point (screen coordinates) */
  x2: number;
  y2: number;
}

export interface PolylineOverlay extends OverlayGeometry {
  type: OverlayType.Polyline;
  /** Array of points (screen coordinates) */
  points: Array<{ x: number; y: number }>;
  /** Whether to close the polyline */
  closed?: boolean;
}

export interface CircleOverlay extends OverlayGeometry {
  type: OverlayType.Circle;
  /** Center point (screen coordinates) */
  cx: number;
  cy: number;
  /** Radius in pixels */
  radius: number;
}

export interface ArcOverlay extends OverlayGeometry {
  type: OverlayType.Arc;
  /** Center point (screen coordinates) */
  cx: number;
  cy: number;
  /** Radius in pixels */
  radius: number;
  /** Start angle in radians */
  startAngle: number;
  /** End angle in radians */
  endAngle: number;
  /** Whether to draw counter-clockwise (default: false) */
  counterClockwise?: boolean;
}

export interface RectangleOverlay extends OverlayGeometry {
  type: OverlayType.Rectangle;
  /** Top-left corner (screen coordinates) */
  x: number;
  y: number;
  /** Width in pixels */
  width: number;
  /** Height in pixels */
  height: number;
}

export interface TextOverlay extends OverlayGeometry {
  type: OverlayType.Text;
  /** Position (screen coordinates) */
  x: number;
  y: number;
  /** Text content */
  text: string;
  /** Font size in pixels */
  fontSize?: number;
  /** Font family */
  fontFamily?: string;
  /** Background color behind text (CSS color). If set, draws a rounded rect background. */
  backgroundColor?: string;
  /** Padding around text when backgroundColor is set (px). Default: 4 */
  padding?: number;
  /** Horizontal text alignment. Default: 'left' */
  textAlign?: CanvasTextAlign;
  /** Vertical text baseline. Default: 'top' */
  textBaseline?: CanvasTextBaseline;
}

export interface EllipseOverlay extends OverlayGeometry {
  type: OverlayType.Ellipse;
  /** Center point (screen coordinates) */
  cx: number;
  cy: number;
  /** Radius in pixels */
  radiusx: number;
  radiusy: number;
  /** Rotation in radians: default is 0 */
  rotation?: number;
}

/**
 * Union type for all basic overlay geometries.
 */
export type AnyOverlay =
  | PointOverlay
  | LineOverlay
  | PolylineOverlay
  | CircleOverlay
  | ArcOverlay
  | RectangleOverlay
  | TextOverlay
  | EllipseOverlay;

// ============== Interactive Overlay Types ==============

/**
 * Base interface for interactive overlays.
 * Extends OverlayGeometry with interaction capabilities.
 */
export interface InteractiveOverlay extends OverlayGeometry {
  /** Marks this as an interactive overlay */
  interactive: true;
  /** Coordinate system for positioning */
  coordinateSystem: CoordinateSystem;
  /** Hit testing radius in pixels */
  hitRadius?: number;
  /** Cursor style when hovered */
  cursor?: string;
  /** Whether currently being dragged */
  isDragging?: boolean;

  /** Defines if overlay accept or not snapOptions */
  snapOptions?: SnapOptions;

  // Drag handlers - called by InteractiveLayer
  /** Called when drag starts on this overlay */
  onDragStart?: (hit: HitTestResult) => void;
  /** Called during drag movement */
  onDragMove?: (
    screenPoint: { x: number; y: number },
    worldPoint: { x: number; y: number; z: number },
  ) => void;
  /** Called when drag ends */
  onDragEnd?: (
    screenPoint: { x: number; y: number },
    worldPoint: { x: number; y: number; z: number },
  ) => void;
}

/**
 * Handle overlay - draggable point at entity locations.
 */
export interface HandleOverlay extends InteractiveOverlay {
  type: OverlayType.Handle;
  /** World coordinates of the handle */
  worldPosition: { x: number; y: number; z: number };
  /** Cached screen position (updated on view change) */
  screenPosition?: { x: number; y: number };
  /** Handle shape */
  shape?: "circle" | "square" | "diamond";
  /** Handle size in pixels */
  size?: number;
  /** Associated entity ID (for transforms) */
  entityId?: string;
  /** Handle role (corner, edge, center, vertex, custom) */
  role?: string;
  /** Callback data for drag operations */
  dragData?: unknown;
}

/**
 * Ruler overlay - graduation ruler along canvas edge.
 */
export interface RulerOverlay extends InteractiveOverlay {
  type: OverlayType.Ruler;
  /** Ruler orientation */
  orientation: "horizontal" | "vertical";
  /** Origin point in world coordinates (0,0 of the ruler) */
  origin: { x: number; y: number };
  /** Ruler position offset from canvas edge in pixels */
  edgeOffset: number;
  /** Ruler thickness in pixels */
  thickness: number;
  /** Handle size in pixels (for hit testing) */
  handleSize?: number;
  /**
   * Vertical ruler only: downward shift of its handle (pixels), which sits at
   * the bottom of the ruler.
   */
  handleOffsetY?: number;
  /**
   * Horizontal ruler only: rightward shift of its handle (pixels), clear of
   * the floating toolbar over the left edge of the canvas.
   */
  handleOffsetX?: number;
  /** Whether the position handle is drawn and hit-tested (default true). */
  showHandle?: boolean;
  /** Pointers that also grab the ruler by its bar, not only by its handle. */
  barGrabbedBy?: readonly PointerType[];
  /** Whether origin marker is being dragged */
  isDraggingOrigin?: boolean;
}

/**
 * Union type for all interactive overlays.
 */
export type AnyInteractiveOverlay = HandleOverlay | RulerOverlay;

// ============== Hit Test Types ==============

/**
 * Result of a hit test against interactive overlays.
 */
export interface HitTestResult {
  /** The overlay that was hit */
  overlay: InteractiveOverlay;
  /** Overlay ID */
  id: string;
  /** Hit point in screen coordinates */
  screenPoint: { x: number; y: number };
  /** Hit point in world coordinates */
  worldPoint: { x: number; y: number; z: number };
  /** Distance from hit point to overlay center */
  distance: number;
  /** Specific hit zone (for rulers: 'handle', 'body'; for handles: 'center') */
  zone?: string;
}

// ============== Ruler Configuration ==============

/**
 * Configuration for ruler appearance and behavior.
 */
export interface RulerConfig {
  /** Graduation mark length in pixels */
  graduationLength: number;
  /** Sub-graduation mark length in pixels */
  subGraduationLength: number;
  /** Number of sub-graduations between main graduations */
  subGraduationCount: number;
  /** Minimum spacing between graduations in pixels */
  minimumSpacing: number;
  /** Available spacing values in world units (must be ordered ascending) */
  spacings: number[];
  /** Label font size in pixels */
  labelFontSize: number;
  /** Ruler background color */
  backgroundColor: string;
  /** Graduation line color */
  lineColor: string;
  /** Label text color */
  textColor: string;
  /** Origin marker color */
  originColor: string;
  /** Ruler thickness in pixels */
  thickness: number;
  /** Handle size in pixels */
  handleSize: number;
  /** Handle border color */
  handleBorderColor: string;
}

/**
 * Default ruler configuration.
 */
export const DEFAULT_RULER_CONFIG: RulerConfig = {
  graduationLength: 12,
  subGraduationLength: 6,
  subGraduationCount: 5,
  minimumSpacing: 12,
  spacings: [
    0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 5, 10, 20, 50, 100, 500, 1000, 5000,
    10000,
  ],
  labelFontSize: 9,
  backgroundColor: "rgba(245, 245, 245, 0.5)",
  lineColor: "rgba(125, 125, 125, 0.8)",
  textColor: "rgba(51, 51, 51, 0.8)",
  originColor: "#ff4444",
  thickness: 25,
  handleSize: 40,
  handleBorderColor: "#ffffff",
};

// ============== Handle Configuration ==============

/**
 * Configuration for handle appearance.
 */
export interface HandleConfig {
  /** Handle size in pixels */
  size: number;
  /** Handle shape */
  shape: "circle" | "square" | "diamond";
  /** Fill color */
  fillColor: string;
  /** Stroke color */
  strokeColor: string;
  /** Stroke width */
  strokeWidth: number;
  /** Fill color when hovered */
  hoverFillColor: string;
  /** Fill color when active/dragging */
  activeFillColor: string;
}

/**
 * Default handle configuration.
 */
export const DEFAULT_HANDLE_CONFIG: HandleConfig = {
  size: 8,
  shape: "square",
  fillColor: "#ffffff",
  strokeColor: "#0088ff",
  strokeWidth: 2,
  hoverFillColor: "#e0f0ff",
  activeFillColor: "#0088ff",
};

// ============== Selection Window Configuration ==============

/**
 * Configuration for selection window/crossing rectangle appearance.
 */
export interface SelectionWindowConfig {
  /** Stroke color (CSS color string) */
  strokeColor: string;
  /** Stroke width in pixels */
  strokeWidth: number;
  /** Fill color (CSS color string with alpha) */
  fillColor: string;
  /** Line dash pattern [dashLength, gapLength] */
  lineDash: number[];
}

/**
 * Default window selection style (left-to-right drag).
 * Blue color, solid border.
 */
export const DEFAULT_WINDOW_SELECTION_STYLE: SelectionWindowConfig = {
  strokeColor: "#0088ff",
  strokeWidth: 1,
  fillColor: "rgba(0, 136, 255, 0.1)",
  lineDash: [],
};

/**
 * Default crossing selection style (right-to-left drag).
 * Green color, dashed border.
 */
export const DEFAULT_CROSSING_SELECTION_STYLE: SelectionWindowConfig = {
  strokeColor: "#00cc00",
  strokeWidth: 1,
  fillColor: "rgba(0, 204, 0, 0.1)",
  lineDash: [4, 4],
};
